import { DelayCodeControllableOptions, IDelayCategory, IDelayCode } from "@vmosolutions/oc-interfaces";
import { useCallback, useMemo } from "react";
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { ISelectFieldProps } from "src/components/FormikComponents/SelectField";
// API Calls
import { formatFunction, parseFunction } from "./helpers";

import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface IDelayCodeSelectorProps extends Omit<ISelectFieldProps, "options"> {
  mosaicPane: HTMLElement;
  valueKey?: string; // valueKey sent back as value - use entire object otherwise.
  showInactive?: boolean;
}

interface IDelayOptions {
  _id: any;
  _old_id: string;
  title: string;
  description: string;
  delayCode: {
    _id: any;
    _old_id: number;
    delayNumber: number;
    title: string;
    description: string;
    division: string;
    requireATA: boolean;
    requireOther: boolean;
    requireTail: boolean;
    controllable: DelayCodeControllableOptions | boolean;
  };
}

/** Sort types list Alphabetically */
const sortTypesByDescription = (a: IDelayOptions, b: IDelayOptions): number => {
  return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
};

/**
 * Sepcialized wrapper for "built-in" oc Delay Codes select field.
 * @param props -
 */
export const DelayCodeSelector = (props: IDelayCodeSelectorProps): JSX.Element => {
  const { mosaicPane, label, name, onChange, valueKey, isMulti, defaultValue, ...selectProps } = props;

  const structures = useSelector((state: IRootState) => state.structures);
  const delayCodes = useMemo(() => {
    const delayCategories = structures?.customerStructure?.delayCategories ?? [];
    if (delayCategories) {
      const tempDelayOptions: IDelayOptions[] = [];
      delayCategories.forEach((cat: IDelayCategory) => {
        cat.delayCodes.forEach((opt: IDelayCode) => {
          tempDelayOptions.push({
            _id: opt._id,
            _old_id: cat._old_id,
            title: cat.title,
            description: cat.description,
            delayCode: opt,
          });
        });
      });
      return tempDelayOptions;
    } else return [];
  }, [structures?.customerStructure?.delayCategories]);

  const options = useMemo(
    () =>
      delayCodes.sort((a: IDelayOptions, b: IDelayOptions) =>
        a.title === b.title ? a.delayCode.delayNumber - b.delayCode.delayNumber : a.title.localeCompare(b.title)
      ),
    [delayCodes]
  );

  // Setup Format And Parse functions (used if valueKey is passed)
  const formatValue = useCallback(
    (value: string) => formatFunction(value, delayCodes, valueKey, isMulti),
    [delayCodes, valueKey, isMulti]
  );
  const parseOption = useCallback((option: string) => parseFunction(option, valueKey, isMulti), [valueKey, isMulti]);

  return (
    <FormikSelectField
      name={name}
      label={label ?? "Delay Code Selector"}
      isMulti={isMulti}
      options={options}
      getOptionLabel={(type: IDelayOptions) =>
        `${type.title.concat(type.delayCode.delayNumber.toString())} - ${type.delayCode.description}`
      }
      getOptionValue={(type: IDelayOptions) => type._id}
      format={(value) => (valueKey ? formatValue(value) : value)}
      parse={(option) => (valueKey ? parseOption(option) : option)}
      onChange={(value: any) => !!onChange && onChange(value)}
      menuPortalTarget={mosaicPane}
      menuPosition={mosaicPane ? "fixed" : undefined}
      isSearchable={true}
      openMenuOnFocus={false}
      isClearable={true}
      placeholder={`Select Delay Code${isMulti && "s"}`}
      defaultValue={defaultValue}
      {...selectProps} // override with any props passed in such as isMulti, format, parse.
    />
  );
};
